/**
 * @file useBinScan.ts
 * @description Разбор скана в поле ячейки через `POST /admin/wms/scan/resolve`
 *   (phase19.22, W14-9; ui-design §8).
 *
 * Уровень адреса знает справочник склада, а не число сегментов: `LOC:A-02`
 * может оказаться и полкой, и ячейкой. Поэтому поле не угадывает, а спрашивает
 * сервер и ведёт себя по ответу:
 *   - ячейка → выбрана (архивная — ошибка «в архиве»);
 *   - полка/стеллаж → плитки ячеек/полок; единственная активная — подставлена
 *     с подписью, откуда она взялась;
 *   - код товара, текст, BOX/ORD → «Это код товара, а здесь нужен адрес».
 * Ошибки — янтарной строкой, выбор не сбрасывается.
 */

import { useCallback, useState } from "react";

import { ApiError } from "../../../lib/api-client";
import {
  wmsScanApi,
  type ResolvedBin,
  type ResolvedBinLevel,
} from "../../../lib/api-wms-scan";
import { toBinScanRaw } from "../../../lib/scan-payload";

/** Выбранная ячейка и пояснение, как она выбрана. */
export interface PickedBin {
  id: string;
  code: string;
  lotsCount: number;
  /** «на полке C-01 одна ячейка» — подставлена с уровня. */
  note?: string;
}

const LEVEL_WORD: Record<ResolvedBinLevel["level"], string> = {
  shelf: "полке",
  rack: "стеллаже",
};

export function useBinScan(warehouseId: string, onPick: (bin: PickedBin) => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [level, setLevel] = useState<ResolvedBinLevel | null>(null);

  const pick = useCallback(
    (bin: ResolvedBin, note?: string) => {
      setLevel(null);
      setError(null);
      onPick({ id: bin.id, code: bin.code, lotsCount: bin.lotsCount, note });
    },
    [onPick],
  );

  const resolve = useCallback(
    async (input: string) => {
      const raw = toBinScanRaw(input);
      if (!raw || !warehouseId) return;
      setBusy(true);
      setError(null);
      try {
        const res = await wmsScanApi.resolve({ raw, warehouseId });
        if (res.kind === "bin" && res.level === "bin") {
          if (!res.bin.isActive) setError(`Ячейка ${res.bin.code} в архиве`);
          else pick(res.bin);
        } else if (res.kind === "bin") {
          if (res.single) pick(res.single, `на ${LEVEL_WORD[res.level]} ${res.address} одна ячейка`);
          else setLevel(res);
        } else {
          setLevel(null);
          setError("Это код товара, а здесь нужен адрес ячейки");
        }
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "Не удалось распознать адрес — повторите скан");
      } finally {
        setBusy(false);
      }
    },
    [warehouseId, pick],
  );

  return {
    busy,
    error,
    level,
    resolve,
    pick,
    /** Раскрыть полку стеллажа: тот же резолвер по её адресу. */
    openShelf: (address: string) => void resolve(`LOC:${address}`),
    closeLevel: () => setLevel(null),
    clearError: () => setError(null),
  };
}
